import * as React from 'react';

import {emptyObject} from '@packages/utilities/fp/utils';
import {useSize, type SizeContextSizeValue} from '@packages/utilities/styled';
import {useDevAlert} from '@packages/systems/spring-hooks';

import {isDefined, renderElement} from '../../utils';
import type {AsProps, HTMLAttributes, SpringTooling} from '../__SharedTypes';
import type {SpringIconType} from '../../icons/Icon';
import type {ButtonProps} from '../Button';
import type {LinkTargets} from '../Link';
import {Spinner} from '../Spinner';

import {IconButtonView} from './IconButton.styles';
import {CloseDefaultIcon} from '../../icons';
import {Tooltip, type TooltipProps} from '../Tooltip';

export type IconButtonProps = AsProps &
  Omit<HTMLAttributes, 'prefix'> &
  SpringTooling & {
    /**
     * The busy state for screen readers.
     */
    ['aria-busy']?: boolean;
    /**
     * The `colorVariant` affects the background, outline, and/or text color of the `IconButton` depending
     * on which variant you use.
     */
    colorVariant?: ButtonProps['colorVariant'];
    /**
     * @deprecated - This prop is no longer supported.
     */
    contrast?: React.ComponentProps<SpringIconType>['contrast'];
    /**
     * The URL target for the Button as a link. If you are using an `IconButton` for navigation, you can
     * specify a url through `href`. This will render as an `<a>` tagin the DOM, but will still have the
     * visual styling of a button.
     */
    href?: string;
    /**
     * The Icon to render within the button.
     */
    Icon:
      | SpringIconType
      | React.ReactElement<React.ComponentProps<SpringIconType>>
      | React.ReactElement<React.ComponentProps<typeof Spinner>>
      | null;
    /**
     * Props to pass through to the underlying Icon component
     */
    iconProps?: Omit<
      React.ComponentProps<SpringIconType>,
      'children' | 'contrast'
    >;
    /**
     * Sets the `disabled` attribute on the underlying button element to `true`, making it unclickable
     * and changing the cursor.
     */
    isDisabled?: boolean;
    /**
     * Sets the loading state for the button.
     */
    isLoading?: boolean;
    /**
     * Sets the selected state for the button. This is used for toggle buttons (typically in a ButtonGroup).
     */
    isSelected?: boolean;
    /**
     * A description of the button's action, used to set the component's aria-label attribute and optional
     * tooltip text, making it available to assistive technologies such as screen readers.
     */
    label: string;
    /**
     * Change button size to override size context.
     *
     * @deprecated - This no longer has any effect
     */
    size?: SizeContextSizeValue;
    /**
     * When `true`, a `Tooltip` will render on hover with the `label` as the content.
     */
    withTooltip?: boolean;
    /**
     * The value of the link's target attribute.
     */
    target?: LinkTargets;
    /**
     * By setting `tooltipProps`, you can further customize the `Tooltip` that renders when `withTooltip`
     * is set to `true`. This includes things like `placement` and `color`.
     */
    tooltipProps?: Omit<TooltipProps, 'children'>;
    /**
     * The button's behavior type.
     */
    type?: 'button' | 'submit' | 'reset';
    /**
     * The variant of the button.
     */
    variant?: ButtonProps['variant'];
    /**
     * The density of the button.
     */
    density?: 'comfortable' | 'compact';
    /**
     * Activates branded styling (larger icon size, fixed sizing that ignores SizeContext).
     * When true, the icon renders at 24px instead of the default 16px.
     */
    isBranded?: boolean;
  };

/**
 * # IconButton
 *
 * A button whose contents are an icon
 *
 * ## Usage
 *
 * ```jsx
 * import {IconButton, PhoneIcon} from '@packages/systems/spring';
 *
 * const Example = () => <IconButton Icon={PhoneIcon} label="Start a call" />
 * ```
 *
 * @component
 */
export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      Icon,
      isBranded,
      withTooltip,
      size: sizeProp,
      label,
      tooltipProps = emptyObject,
      iconProps = emptyObject,
      ...props
    }: IconButtonProps,
    ref
  ) => {
    useDevAlert(
      !isDefined(Icon),
      'No Icon was specified, displaying CloseDefaultIcon as a placeholder'
    );
    // Assigning the default icon here instead of in params so react-docgen still recognizes the prop as required in the
    // docs
    if (!isDefined(Icon)) Icon = CloseDefaultIcon;

    const size = useSize(sizeProp);

    const iconSize = isBranded ? 24 : 16;

    const iconButtonMarkup = (
      <IconButtonView
        aria-label={label}
        {...props}
        isBranded={isBranded}
        ref={ref}
        size={size}
      >
        {renderElement(Icon, {size: iconSize, ...iconProps})}
      </IconButtonView>
    );

    if (withTooltip) {
      return (
        <Tooltip asChild label={label} {...tooltipProps}>
          {iconButtonMarkup}
        </Tooltip>
      );
    }
    return iconButtonMarkup;
  }
);

IconButton.displayName = 'IconButton';
IconButton.toString = IconButtonView.toString;

export default IconButton;
